/**
 * Author: Claude Code
 * Generation: Progress aggregation for loading UI (client x server)
 * Version: v3.2.1
 * Modified: 2025-10-21
 *
 * Purpose: Combine client (asset) and server (room) loading progress; cap at 95% until authoritative data arrives.
 */

type Listener = (progress: number, detail: { client: number; server: number }) => void; // Notify combined + components

class ProgressAggregator {
  private client: number = 0; // 0..100
  private server: number = 0; // 0..100
  private wClient = 0.4; // Weight for client progress
  private wServer = 0.6; // Weight for server progress
  private cap = 95; // Cap before authoritative completion
  private listeners: Set<Listener> = new Set();

  setWeights(wClient: number, wServer: number): void { this.wClient = Math.max(0, Math.min(1, wClient)); this.wServer = Math.max(0, Math.min(1, wServer)); }
  setCap(cap: number): void { this.cap = Math.max(0, Math.min(100, Math.floor(cap))); }
  reset(): void { this.client = 0; this.server = 0; this.emit(); }

  setClientProgress(pct: number): void { const p = this.clamp(pct); if (p !== this.client) { this.client = p; this.emit(); } }
  setServerProgress(pct: number): void { const p = this.clamp(pct); if (p !== this.server) { this.server = p; this.emit(); } }

  getCombined(): number { return this.clamp(Math.round(this.wClient * this.client + this.wServer * this.server), 0, this.cap); }
  getDetail(): { client: number; server: number } { return { client: this.client, server: this.server }; }

  subscribe(fn: Listener): () => void { this.listeners.add(fn); fn(this.getCombined(), this.getDetail()); return () => this.listeners.delete(fn); }

  private emit(): void { const progress = this.getCombined(); const detail = this.getDetail(); this.listeners.forEach(fn => { try { fn(progress, detail); } catch {} }); }
  private clamp(v: number, min: number = 0, max: number = 100): number { if (!Number.isFinite(v)) return min; return Math.max(min, Math.min(max, Math.floor(v))); }
}

const progressAggregator = new ProgressAggregator();
export default progressAggregator;
export type { Listener };

